import { ArrowRight, ChevronDown } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { ENTITY_ICON_COMPONENTS } from '@/components/neo4j/cytoscape/entity-icons'
import { useIsMobile } from '@/hooks/use-mobile'
import type { TopEntitiesResponse, TopEntityEntry } from '@/services/api/graph'
import { SectionHeader } from '../primitives/SectionHeader'
import { LeaderboardRow } from './LeaderboardRow'

export interface EntityLeaderboardProps {
  caseId: string
  data: TopEntitiesResponse | null
  onRowClick?: (entityType: string, entity: TopEntityEntry) => void
  onSeeAll?: () => void
}

function storageKey(caseId: string) {
  return `insights:leaderboard-tab:${caseId}`
}

export function EntityLeaderboard({ caseId, data, onRowClick, onSeeAll }: EntityLeaderboardProps) {
  const isMobile = useIsMobile()
  const groups = data?.entity_types ?? []
  const firstType = groups[0]?.entity_type ?? null

  const [activeType, setActiveType] = useState<string | null>(() => {
    if (typeof window === 'undefined') return firstType
    const stored = window.localStorage.getItem(storageKey(caseId))
    return stored ?? firstType
  })

  useEffect(() => {
    const validTypes = groups.map(g => g.entity_type)
    if (activeType != null && !validTypes.includes(activeType)) {
      setActiveType(firstType)
    } else if (activeType == null && firstType != null) {
      setActiveType(firstType)
    }
  }, [activeType, firstType, groups])

  const handleSelect = useCallback(
    (type: string) => {
      setActiveType(type)
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(storageKey(caseId), type)
      }
    },
    [caseId]
  )

  if (groups.length === 0) {
    return (
      <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-4">
        <SectionHeader
          title="Top entities by type"
          subLabel="Ranked by investigation score"
          guide={{
            to: '/docs/investigation-analysis#investigation-score',
            hint: 'Entities ranked by a composite investigation score.',
          }}
        />
        <div className="rounded-lg border border-dashed border-[var(--border)] p-8 text-center">
          <p className="text-xs text-[var(--muted-foreground)]">
            No ranked entities yet — run Recompute to score the case.
          </p>
        </div>
      </div>
    )
  }

  const activeGroup = groups.find(g => g.entity_type === activeType) ?? groups[0]

  return (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-4">
      <SectionHeader
        title="Top entities by type"
        subLabel="Ranked by investigation score · flags: suspect (shield), bridge (zap)"
        guide={{
          to: '/docs/investigation-analysis#investigation-score',
          hint: 'Entities ranked by a composite of evidence diversity, target proximity, source diversity, and connections.',
        }}
        actions={
          onSeeAll && (
            <button
              type="button"
              onClick={onSeeAll}
              className="text-xs text-[var(--primary)] font-medium hover:underline inline-flex items-center gap-1"
            >
              See all entity types <ArrowRight className="size-3" />
            </button>
          )
        }
      />

      {/* Tab strip / Select fallback */}
      {isMobile ? (
        <div className="relative mb-2">
          <select
            value={activeGroup.entity_type}
            onChange={e => handleSelect(e.target.value)}
            className="w-full h-9 appearance-none rounded-lg border border-[var(--input)] bg-transparent pl-2.5 pr-8 text-xs text-[var(--foreground)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          >
            {groups.map(g => (
              <option key={g.entity_type} value={g.entity_type}>
                {g.entity_type} · {g.total_count}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 size-3.5 text-[var(--muted-foreground)]" />
        </div>
      ) : (
        <div className="flex items-center gap-1 border-b border-[var(--border)] mb-2 overflow-x-auto">
          {groups.map(g => {
            const Icon = ENTITY_ICON_COMPONENTS[g.entity_type]
            const active = g.entity_type === activeGroup.entity_type
            return (
              <button
                type="button"
                key={g.entity_type}
                onClick={() => handleSelect(g.entity_type)}
                className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 whitespace-nowrap focus:outline-none focus-visible:text-[var(--foreground)] ${
                  active
                    ? 'border-[var(--primary)] text-[var(--foreground)]'
                    : 'border-transparent text-[var(--muted-foreground)] hover:text-[var(--foreground)]'
                }`}
              >
                {Icon && <Icon className="size-3.5" />}
                {g.entity_type}
                <span className="inline-flex h-4 items-center rounded-full px-1.5 text-3xs font-semibold bg-[var(--muted)]/60">
                  {g.total_count}
                </span>
              </button>
            )
          })}
        </div>
      )}

      {/* Rows */}
      <div className="space-y-0.5">
        {activeGroup.top_entities.map((e, idx) => (
          <LeaderboardRow
            key={e.id}
            row={e}
            rank={idx + 1}
            entityType={activeGroup.entity_type}
            onClick={() => onRowClick?.(activeGroup.entity_type, e)}
          />
        ))}
      </div>
    </div>
  )
}
